import * as XLSX from "xlsx";
import { db } from "../firebase";
import { collection, addDoc, getDocs, query, where, updateDoc } from "firebase/firestore";

function excelDataToISO(serial) {
  if (!serial || typeof serial !== "number") return "";
  const utc_days = Math.floor(serial - 25569);
  const utc_value = utc_days * 86400;
  const date = new Date(utc_value * 1000);
  return date.toISOString().split("T")[0];
}

function excelOraToHHMM(serial) {
  if (!serial || typeof serial !== "number") return "";
  const totalMinutes = Math.round(serial * 24 * 60);
  const hours = Math.floor(totalMinutes / 60) % 24;
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

function mappaRiga(riga, dataISO, clienteOriginale) {
  const anno = riga[16] ? parseInt(riga[16]) : new Date(dataISO).getFullYear();
  const annoCorrente = new Date().getFullYear();

  return {
    titolo: riga[7] || "",
    dataInizio: dataISO,
    oraInizio: excelOraToHHMM(riga[5]),
    durata: riga[6] ? String(riga[6]) : "",
    clienteNomeStorico: clienteOriginale,
    tipologiaEvento: riga[8] ? [riga[8]] : [],
    tecnologie: riga[9] ? [riga[9]] : [],
    tipoEvento: riga[10] === "Remoto" ? "online" : riga[10] === "Ibrido" ? "ibrido" : "presenza",
    oreAlgores: riga[12] ? String(riga[12]) : "",
    oreCollaboratori: riga[13] ? String(riga[13]) : "",
    preventivoRif: riga[14] ? String(riga[14]) : "",
    importoVendita: riga[15] ? String(riga[15]) : "",
    stato: anno >= annoCorrente ? "fatturato" : "archiviato",
    daStorico: true,
    anno,
    mese: riga[17] ? parseInt(riga[17]) : new Date(dataISO).getMonth() + 1,
  };
}

// Confronta due eventi e restituisce i campi che differiscono
function calcolaDifferenze(esistente, nuovo) {
  const campiDaConfronto = [
    "oraInizio", "durata", "clienteNomeStorico", "tipologiaEvento",
    "tecnologie", "tipoEvento", "oreAlgores", "oreCollaboratori",
    "preventivoRif", "importoVendita", "anno", "mese"
  ];

  const differenze = {};
  for (const campo of campiDaConfronto) {
    const vecchio = JSON.stringify(esistente[campo] ?? "");
    const nuovoVal = JSON.stringify(nuovo[campo] ?? "");
    if (vecchio !== nuovoVal) {
      differenze[campo] = nuovo[campo];
    }
  }
  return differenze;
}

export async function importaStorico(file, onProgress) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });

        const sheet = workbook.Sheets["TabellaSorgente"];
        if (!sheet) {
          reject(new Error("Foglio 'TabellaSorgente' non trovato"));
          return;
        }

        const righe = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        const dati = righe.slice(1).filter((r) => r[0] && r[1]);

        let importati = 0;
        let aggiornati = 0;
        let saltati = 0;
        const totale = dati.length;

        for (let i = 0; i < dati.length; i++) {
          const riga = dati[i];
          const dataISO = excelDataToISO(riga[1]);
          const titoloOriginale = riga[7] || "";
          const clienteOriginale = riga[3] || "";

          if (!dataISO || !titoloOriginale) {
            saltati++;
            if (onProgress) onProgress(i + 1, totale, importati, aggiornati, saltati);
            continue;
          }

          const nuovoEvento = mappaRiga(riga, dataISO, clienteOriginale);

          // Cerca evento esistente per titolo e data
          const esistentiSnap = await getDocs(query(
            collection(db, "eventi"),
            where("titolo", "==", titoloOriginale),
            where("dataInizio", "==", dataISO)
          ));

          if (!esistentiSnap.empty) {
            // Evento esistente — confronta i campi
            const docEsistente = esistentiSnap.docs[0];
            const datiEsistenti = docEsistente.data();
            const differenze = calcolaDifferenze(datiEsistenti, nuovoEvento);

            if (Object.keys(differenze).length > 0) {
              // Ci sono differenze — aggiorna
              await updateDoc(docEsistente.ref, differenze);
              aggiornati++;
            } else {
              saltati++;
            }
          } else {
            // Evento non esistente — crea
            await addDoc(collection(db, "eventi"), nuovoEvento);
            importati++;
          }

          if (onProgress) onProgress(i + 1, totale, importati, aggiornati, saltati);
        }

        resolve({ importati, aggiornati, saltati, totale });
      } catch (error) {
        reject(error);
      }
    };
    reader.readAsArrayBuffer(file);
  });
}